import { apiClient } from '@/services/Axios';
import {
  mapLocalAssetFiltersToV1,
  toLegacyDetail,
  toLegacyLocalAssetsList,
  unwrapV1,
  v1Delete,
  v1Get,
  v1Patch,
  v1Post,
  v1Put,
} from '@/services/v1Api';

const LOCAL_ASSETS_V1 = '/api/v1/local-assets';
const STOCK_V1 = '/api/v1/stock';
const LOCATIONS_V1 = '/api/v1/locations';

/**
 * Central service for local asset and stock API calls (v1).
 */
export class LocalAssetService {
  static async list(filters = {}) {
    const { data, meta } = await v1Get(LOCAL_ASSETS_V1, mapLocalAssetFiltersToV1(filters));
    return toLegacyLocalAssetsList(data, meta);
  }

  static async get(assetId) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}`);
    return toLegacyDetail(data);
  }

  static async create(payload) {
    const { data } = await v1Post(LOCAL_ASSETS_V1, payload);
    return toLegacyDetail(data);
  }

  static async update(assetId, payload) {
    const { data } = await v1Patch(`${LOCAL_ASSETS_V1}/${assetId}`, payload);
    return toLegacyDetail(data);
  }

  static async uploadImage(assetId, file, { onUploadProgress } = {}) {
    const formData = new FormData();
    formData.append('image', file);
    const response = await apiClient.post(`${LOCAL_ASSETS_V1}/${assetId}/upload-image`, formData, {
      onUploadProgress,
    });
    const { data } = unwrapV1(response);
    return data;
  }

  static async deleteImage(assetId) {
    const { data } = await v1Delete(`${LOCAL_ASSETS_V1}/${assetId}/delete-image`);
    return data;
  }

  static async getImageSuggestions(assetId, limit = 5) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/image-suggestions`, { limit });
    return toLegacyDetail(data);
  }

  static async applySuggestedImage(assetId, payload) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/apply-suggested-image`, payload);
    return toLegacyDetail(data);
  }

  static async generateBulkBarcode(assetId) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/generate-bulk-barcode`);
    return toLegacyDetail(data);
  }

  static async getLabelConfig(assetId) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/label-config`);
    return toLegacyDetail(data);
  }

  static async saveLabelConfig(assetId, config) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/label-config`, config);
    return toLegacyDetail(data);
  }

  static async updateTimings(assetId, payload) {
    const { data } = await v1Patch(`${LOCAL_ASSETS_V1}/${assetId}/timings`, payload);
    return toLegacyDetail(data);
  }

  static async updateCaseDetails(assetId, payload) {
    const { data } = await v1Patch(`${LOCAL_ASSETS_V1}/${assetId}/case-details`, payload);
    return toLegacyDetail(data);
  }

  static async getSerialStock(assetId) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/stock`);
    return data;
  }

  static async getStockTransactions(assetId) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/stock-transactions`);
    return data;
  }

  static async getBulkStock(assetId) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/bulk-stock`);
    return data;
  }

  static async addBulkStock(assetId, payload) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/bulk-stock`, {
      local_asset_id: parseInt(assetId, 10),
      ...payload,
    });
    return data;
  }

  static async getCaseContents(assetId) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/case/contents`);
    return toLegacyDetail(Array.isArray(data) ? data : data?.contents ?? data);
  }

  static async addCaseContents(assetId, payload) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/case/contents`, payload);
    return toLegacyDetail(data);
  }

  static async deleteCaseContent(assetId, contentId) {
    await v1Delete(`${LOCAL_ASSETS_V1}/${assetId}/case/contents/${contentId}`);
  }

  static async createCase(assetId, caseData) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/case`, caseData);
    return toLegacyDetail(data);
  }

  static async listCases(filters = {}) {
    const { data } = await v1Get(`${LOCAL_ASSETS_V1}/cases`, filters);
    return toLegacyDetail(Array.isArray(data) ? data : []);
  }

  static async getSupplierCosts(assetId) {
    const { data, meta } = await v1Get(`${LOCAL_ASSETS_V1}/${assetId}/supplier-costs`);
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      warehouse_address: meta?.warehouse_address ?? null,
    };
  }

  static async createSupplierCost(assetId, payload) {
    const { data } = await v1Post(`${LOCAL_ASSETS_V1}/${assetId}/supplier-costs`, payload);
    return toLegacyDetail(data);
  }

  static async updateSupplierCost(assetId, costId, payload) {
    const { data } = await v1Put(`${LOCAL_ASSETS_V1}/${assetId}/supplier-costs/${costId}`, payload);
    return toLegacyDetail(data);
  }

  static async deleteSupplierCost(assetId, costId) {
    await v1Delete(`${LOCAL_ASSETS_V1}/${assetId}/supplier-costs/${costId}`);
  }

  static async createMasterCase(payload) {
    const { data } = await v1Post('/api/v1/master-cases', payload);
    return toLegacyDetail(data);
  }

  static async checkAvailabilityMultiple(payload) {
    const { data } = await v1Post('/api/v1/availability/check-multiple', payload);
    return toLegacyDetail(data);
  }
}

export class StockService {
  static async getLocations() {
    const { data } = await v1Get(LOCATIONS_V1);
    return data;
  }

  static async getStockItem(stockId) {
    const { data } = await v1Get(`${STOCK_V1}/${stockId}`);
    return data;
  }

  static async createStock(payload) {
    const { data } = await v1Post(STOCK_V1, payload);
    return data;
  }

  static async updateStock(stockId, payload) {
    const { data } = await v1Put(`${STOCK_V1}/${stockId}`, payload);
    return data;
  }

  static async getNextAssetNumber(payload) {
    const { data } = await v1Post(`${STOCK_V1}/next-asset-number`, payload);
    return data;
  }

  static async canRecover(stockId) {
    const { data } = await v1Get(`${STOCK_V1}/${stockId}/can-recover`);
    return data;
  }

  static async recover(stockId, payload = {}) {
    const { data } = await v1Post(`${STOCK_V1}/${stockId}/recover`, payload);
    return data;
  }

  static async dispose(stockId, payload = {}) {
    const { data } = await v1Post(`${STOCK_V1}/${stockId}/dispose`, payload);
    return data;
  }
}

export default LocalAssetService;
